import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheck } from 'react-icons/fa';
import { useInView } from '../../hooks/useInView';
import { practiceAreas } from '../../data/practiceAreas';

interface PracticeAreaLayoutProps {
  title: string;
  subtitle: string;
  heroImage: string;
  slug: string;
  overview: React.ReactNode;
  services: string[];
  whyUsTitle: string;
  whyUsDescription: string;
  whyUsFeatures: { icon: React.ReactNode; title: string; description: string }[];
  children?: React.ReactNode;
}

const PracticeAreaLayout: React.FC<PracticeAreaLayoutProps> = ({
  title,
  subtitle,
  heroImage,
  slug,
  overview,
  services,
  whyUsTitle,
  whyUsDescription,
  whyUsFeatures,
  children,
}) => {
  const overviewSection = useInView();
  const whyUsSection = useInView();
  const relatedSection = useInView();

  const relatedAreas = practiceAreas.filter((a) => a.slug !== slug);

  return (
    <div className="practice-area-page">
      {/* Hero */}
      <section
        className="pa-hero"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="pa-hero-content">
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
      </section>

      {/* Overview + Services */}
      <section className="pa-overview">
        <div
          ref={overviewSection.ref}
          className={`pa-overview-inner ${overviewSection.isVisible ? 'visible' : ''}`}
        >
          <div className="overview-content">
            <h2>{title} Services</h2>
            {overview}
          </div>
          <div className="overview-services">
            <h3>Our Services Include</h3>
            <ul>
              {services.map((service) => (
                <li key={service}>
                  <FaCheck />
                  {service}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Additional content slot */}
      {children}

      {/* Why Us */}
      <section className="pa-why-us">
        <div
          ref={whyUsSection.ref}
          className={`pa-why-us-inner ${whyUsSection.isVisible ? 'visible' : ''}`}
        >
          <h2>{whyUsTitle}</h2>
          <p>{whyUsDescription}</p>
          <div className="why-us-features">
            {whyUsFeatures.map((feature) => (
              <div key={feature.title} className="feature">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pa-cta">
        <div className="pa-cta-inner">
          <h2>Ready to Discuss Your {title} Needs?</h2>
          <p>Our experienced team is here to help. Contact us for a consultation.</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">
              Contact Us <FaArrowRight />
            </Link>
            <a href="tel:+14169291103" className="btn-secondary">
              Call (416) 929-1103
            </a>
          </div>
        </div>
      </section>

      {/* Related Practice Areas */}
      <section className="pa-related">
        <div
          ref={relatedSection.ref}
          className={`pa-related-inner ${relatedSection.isVisible ? 'visible' : ''}`}
        >
          <div className="related-header">
            <h2>Other Practice Areas</h2>
            <p>Explore our full range of legal services.</p>
          </div>
          <div className="related-grid">
            {relatedAreas.map((area) => (
              <Link
                key={area.slug}
                to={`/practice-areas/${area.slug}`}
                className="related-card"
              >
                <div
                  className="related-card-image"
                  style={{ backgroundImage: `url(${area.backgroundImage})` }}
                />
                <div className="related-card-content">
                  <h3>{area.title}</h3>
                  <p>{area.shortDescription}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PracticeAreaLayout;
